import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import { getIsWebCrawler } from "@insite/client-framework/Common/WebCrawler";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link from "@insite/mobius/Link";
import Tab, { TabProps } from "@insite/mobius/Tab";
import TabGroup from "@insite/mobius/TabGroup";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import parse from "html-react-parser";
import truncate from "lodash/truncate";
import * as React from "react";
import { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, FlattenSimpleInterpolation } from "styled-components";

const enum fields {
    showDescription = "showDescription",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showDescription]: boolean;
    };
}

type Props = WidgetProps & HasProduct & OwnProps;


export interface ProductDetailsSpecificationsStyles {
    attributeTypeValuesText?: TypographyPresentationProps;
    container?: InjectableCss;
    titleStyle?: InjectableCss;
}

export const specificationsStyles: ProductDetailsSpecificationsStyles = {
    attributeTypeValuesText: {
        css: css`
            font-family: Poppins;
            font-weight: ${TYPOGRAPHY.weight.base};
            font-size: ${TYPOGRAPHY.size.small};
            line-height: 28px;
            color: #262525;
        `,
    },
    container: {
        css: css`
            width: 100%;
            height: 100%;
            margin: 20px 0px;
        `,
    },
    titleStyle: {
        css: css`
            font-family: Poppins;
            font-weight: 600;
            font-size: 24px;
            line-height: 32px;
            color: #000;
            margin: 25px 0px;
        `
    }
};

const styles = specificationsStyles;

const CENProductDetailsSpecificationOverview: React.FC<Props> = ({
    product: { content, properties, specifications },
    fields: { showDescription },
}) => {
    const description = content?.htmlContent ? content?.htmlContent : properties.brandHtmlContent;
    if (!description) {
        return null;
    }
    const renderingDescription = description.split("</head>");

    if (!renderingDescription) {
        return null;
    }

    return (
        <>
            <StyledWrapper {...styles.container}>
                {renderingDescription && showDescription && (
                    <StyledWrapper key="Overview">
                        <StyledWrapper>
                            <Typography {...styles.titleStyle} as="p">
                                {translate("Overview:")}
                            </Typography>
                            <Typography {...styles.attributeTypeValuesText} as="div">
                                {parse(transformContentHtml(description), parserOptions)}
                            </Typography>
                        </StyledWrapper>
                    </StyledWrapper>
                )}

                {specifications &&
                    specifications.filter(item => item.htmlContent && item.htmlContent.trim() !== "").length > 0 && (
                        <>
                            {specifications
                                .filter(item => item.htmlContent && item.htmlContent.trim() !== "")
                                .map((item, index) => {
                                    return (
                                        <StyledWrapper key={item.name || index}>
                                            <Typography {...styles.titleStyle} as="p">
                                                {`${item.nameDisplay || item.name}:`}
                                            </Typography>

                                            <Typography {...styles.attributeTypeValuesText}>
                                                {parse(transformContentHtml(item.htmlContent), parserOptions)}
                                            </Typography>
                                        </StyledWrapper>
                                    );
                                })}
                        </>
                    )}
            </StyledWrapper>

        </>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(connect(null)(CENProductDetailsSpecificationOverview)),
    definition: {
        displayName: "CEN Product Overview & Specification tab",
        group: "Product Details",
        fieldDefinitions: [
            {
                name: fields.showDescription,
                displayName: "Show Description",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 1,
            },
        ],
    },
};

export default widgetModule;
